import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, RefreshCw, Search, Filter } from 'lucide-react';
import apiService from '../../services/apiService';



const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Page = styled.div`
  min-height: 100vh; background: #0a0a0f; color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif; padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #1e2030;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer;
  padding: 8px; border-radius: 8px; transition: all 0.2s;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const PageTitle = styled.h1`
  font-size: 20px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #10b981, #06b6d4);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const NavTabs = styled.div`display: flex; gap: 8px; margin-left: auto;`;
const NavTab = styled.button`
  background: ${p => p.active ? 'rgba(16,185,129,0.15)' : 'transparent'};
  border: 1px solid ${p => p.active ? '#10b981' : '#1e2030'};
  color: ${p => p.active ? '#10b981' : '#64748b'};
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  &:hover { border-color: #10b981; color: #10b981; }
`;
const RefreshBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  background: rgba(16,185,129,0.1); border: 1px solid #10b981;
  color: #10b981; padding: 8px 14px; border-radius: 8px;
  font-size: 12px; font-weight: 600; cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const SpinIcon = styled(RefreshCw)`animation: ${p => p.$spinning ? css`${spin} 1s linear infinite` : 'none'};`;

const FilterBar = styled.div`
  display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap;
`;
const FilterBtn = styled.button`
  background: ${p => p.active ? 'rgba(16,185,129,0.15)' : '#111827'};
  border: 1px solid ${p => p.active ? '#10b981' : '#1e2030'};
  color: ${p => p.active ? '#10b981' : '#64748b'};
  padding: 6px 14px; border-radius: 8px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
`;
const SearchInput = styled.input`
  background: #111827; border: 1px solid #1e2030; border-radius: 8px;
  color: #e2e8f0; padding: 6px 12px; font-size: 13px;
  outline: none; width: 180px;
  &:focus { border-color: #10b981; }
  &::placeholder { color: #475569; }
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 28px;
  animation: ${fadeIn} 0.4s ease;
`;
const TimelineLine = styled.div`
  position: absolute; left: 8px; top: 0; bottom: 0;
  width: 2px; background: linear-gradient(to bottom, #10b981, #1e2030);
`;
const TradeCard = styled.div`
  position: relative;
  background: #111827;
  border: 1px solid ${p =>
    p.action?.includes('ENTER') || p.action?.includes('PYRAMID') ? '#10b981' :
    p.action === 'CLOSE_STOP' ? '#ef4444' :
    p.action === 'CLOSE_EXIT' ? '#f97316' :
    '#1e2030'};
  border-radius: 14px; padding: 18px 20px; margin-bottom: 14px;
  transition: all 0.2s;
  &:hover { transform: translateX(2px); }
`;
const TradeDot = styled.div`
  position: absolute;
  left: -29px; top: 20px;
  width: 12px; height: 12px; border-radius: 50%; border: 2px solid #0a0a0f;
  background: ${p =>
    p.action?.includes('ENTER') || p.action?.includes('PYRAMID') ? '#10b981' :
    p.action === 'CLOSE_STOP' ? '#ef4444' :
    p.action === 'CLOSE_EXIT' ? '#f97316' :
    '#64748b'};
  z-index: 1;
`;
const TradeHeader = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;
`;
const TradeTitle = styled.div`
  font-weight: 700; font-size: 15px; color: #e2e8f0;
`;
const TradeMeta = styled.div`
  font-size: 11px; color: #64748b; margin-top: 3px;
`;
const ActionBadge = styled.span`
  font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 8px;
  background: ${p =>
    p.action?.includes('ENTER') ? 'rgba(16,185,129,0.15)' :
    p.action?.includes('PYRAMID') ? 'rgba(245,158,11,0.15)' :
    p.action === 'CLOSE_STOP' ? 'rgba(239,68,68,0.15)' :
    p.action === 'CLOSE_EXIT' ? 'rgba(249,115,22,0.15)' :
    'rgba(100,116,139,0.15)'};
  color: ${p =>
    p.action?.includes('ENTER') ? '#10b981' :
    p.action?.includes('PYRAMID') ? '#f59e0b' :
    p.action === 'CLOSE_STOP' ? '#ef4444' :
    p.action === 'CLOSE_EXIT' ? '#f97316' :
    '#64748b'};
`;
const TradeGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px;
`;
const TradeField = styled.div`font-size: 12px;`;
const FieldLabel = styled.div`color: #64748b; margin-bottom: 3px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em;`;
const FieldVal = styled.div`color: ${p => p.color || '#e2e8f0'}; font-weight: 600;`;
const ReasonBox = styled.div`
  margin-top: 12px; font-size: 12px; color: #64748b;
  background: #0a0a0f; border-radius: 8px; padding: 8px 12px; border-left: 2px solid #1e2030;
`;

const ACTION_LABEL = {
  ENTER_1: '신규 진입 (Unit 1)',
  PYRAMID_2: '피라미딩 (Unit 2)',
  PYRAMID_3: '피라미딩 (Unit 3)',
  PYRAMID_4: '피라미딩 (Unit 4)',
  CLOSE_STOP: '스탑로스 청산',
  CLOSE_EXIT: '청산 신호 종료',
};

const EmptyState = styled.div`text-align: center; padding: 80px 20px; color: #475569;`;

function TurtleJournalPage() {
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('ALL');
  const [filterSystem, setFilterSystem] = useState('ALL');
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getTurtleTrades(300);
      setTrades(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const fmt = n => n != null ? Number(n).toLocaleString() : '-';
  const fmtPct = n => n != null ? `${n > 0 ? '+' : ''}${Number(n).toFixed(2)}%` : '-';

  const filtered = trades.filter(t => {
    if (filterAction !== 'ALL' && !t.action?.includes(filterAction)) return false;
    if (filterSystem !== 'ALL' && t.system !== filterSystem) return false;
    if (search && !t.name?.includes(search) && !t.symbol?.includes(search)) return false;
    return true;
  });

  const totalPnl = trades.filter(t => ['CLOSE_STOP', 'CLOSE_EXIT'].includes(t.action))
    .reduce((s, t) => s + (t.pnlAmount || 0), 0);

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/turtle')}><ArrowLeft size={18} /></BackBtn>
        <PageTitle>📋 거래 일지</PageTitle>
        <NavTabs>
          <NavTab onClick={() => navigate('/labs/turtle')}>대시보드</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/positions')}>포지션</NavTab>
          <NavTab active onClick={() => navigate('/labs/turtle/journal')}>거래일지</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/multi-sim')}>멀티 시뮬</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/screener')}>스크리너</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/settings')}><Settings size={13} /></NavTab>
        </NavTabs>
        <RefreshBtn onClick={loadData} disabled={loading}>
          <SpinIcon size={13} $spinning={loading} />
          새로고침
        </RefreshBtn>
      </TopBar>

      {/* 요약 통계 */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: '총 거래수', val: filtered.length + '건' },
          { label: '진입 횟수', val: filtered.filter(t => t.action?.includes('ENTER') || t.action?.includes('PYRAMID')).length + '건' },
          { label: '청산 횟수', val: filtered.filter(t => ['CLOSE_STOP', 'CLOSE_EXIT'].includes(t.action)).length + '건' },
          { label: '총 실현 손익', val: fmt(totalPnl) + '원', color: totalPnl >= 0 ? '#10b981' : '#ef4444' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#111827', border: '1px solid #1e2030', borderRadius: 10, padding: '12px 18px' }}>
            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: s.color || '#e2e8f0' }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* 필터 */}
      <FilterBar>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Search size={13} color="#64748b" />
          <SearchInput
            placeholder="종목명 검색..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <FilterBtn active={filterAction === 'ALL'} onClick={() => setFilterAction('ALL')}>전체</FilterBtn>
        <FilterBtn active={filterAction === 'ENTER'} onClick={() => setFilterAction('ENTER')}>진입</FilterBtn>
        <FilterBtn active={filterAction === 'PYRAMID'} onClick={() => setFilterAction('PYRAMID')}>피라미딩</FilterBtn>
        <FilterBtn active={filterAction === 'CLOSE'} onClick={() => setFilterAction('CLOSE')}>청산</FilterBtn>
        <div style={{ display: 'flex', gap: 6, marginLeft: 8, borderLeft: '1px solid #1e2030', paddingLeft: 8 }}>
          <FilterBtn active={filterSystem === 'ALL'} onClick={() => setFilterSystem('ALL')}>ALL</FilterBtn>
          <FilterBtn active={filterSystem === 'SYS1'} onClick={() => setFilterSystem('SYS1')}>SYS1</FilterBtn>
          <FilterBtn active={filterSystem === 'SYS2'} onClick={() => setFilterSystem('SYS2')}>SYS2</FilterBtn>
        </div>
      </FilterBar>

      {loading ? (
        <EmptyState>로딩 중...</EmptyState>
      ) : filtered.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>거래 내역이 없습니다</div>
          <div style={{ fontSize: 13, marginTop: 8 }}>신호 스캔 실행 후 거래가 발생하면 여기에 기록됩니다</div>
        </EmptyState>
      ) : (
        <Timeline>
          <TimelineLine />
          {filtered.map(trade => (
            <TradeCard key={trade.id} action={trade.action}>
              <TradeDot action={trade.action} />
              <TradeHeader>
                <div>
                  <TradeTitle>
                    {trade.name} <span style={{ color: '#64748b', fontWeight: 400 }}>({trade.symbol})</span>
                  </TradeTitle>
                  <TradeMeta>
                    {trade.date} · {trade.createdAt?.slice(11, 16)} KST
                    · <span style={{ color: trade.system === 'SYS2' ? '#06b6d4' : '#10b981' }}>{trade.system}</span>
                  </TradeMeta>
                </div>
                <ActionBadge action={trade.action}>
                  {ACTION_LABEL[trade.action] || trade.action}
                </ActionBadge>
              </TradeHeader>

              <TradeGrid>
                <TradeField>
                  <FieldLabel>체결가</FieldLabel>
                  <FieldVal>{fmt(trade.price)}원</FieldVal>
                </TradeField>
                <TradeField>
                  <FieldLabel>수량</FieldLabel>
                  <FieldVal>{fmt(trade.quantity)}주</FieldVal>
                </TradeField>
                <TradeField>
                  <FieldLabel>N (ATR)</FieldLabel>
                  <FieldVal color="#f59e0b">{fmt(trade.N)}원</FieldVal>
                </TradeField>
                {trade.stopLoss > 0 && (
                  <TradeField>
                    <FieldLabel>스탑로스</FieldLabel>
                    <FieldVal color="#ef4444">{fmt(trade.stopLoss)}원</FieldVal>
                  </TradeField>
                )}
                {trade.unitNo && (
                  <TradeField>
                    <FieldLabel>Unit 번호</FieldLabel>
                    <FieldVal>Unit {trade.unitNo}/4</FieldVal>
                  </TradeField>
                )}
                {trade.pnlAmount !== undefined && trade.pnlAmount !== 0 && (
                  <TradeField>
                    <FieldLabel>실현 손익</FieldLabel>
                    <FieldVal color={trade.pnlAmount > 0 ? '#10b981' : '#ef4444'}>
                      {fmt(trade.pnlAmount)}원 ({fmtPct(trade.pnlRate)})
                    </FieldVal>
                  </TradeField>
                )}
                <TradeField>
                  <FieldLabel>당시 자산</FieldLabel>
                  <FieldVal color="#94a3b8">{fmt(trade.accountEquityAtTime)}원</FieldVal>
                </TradeField>
              </TradeGrid>

              {trade.reason && (
                <ReasonBox>
                  💬 {trade.reason}
                </ReasonBox>
              )}
            </TradeCard>
          ))}
        </Timeline>
      )}
    </Page>
  );
}

export default TurtleJournalPage;
